import { captureErrorInSentry } from 'utils/sentry';
import {
  AssignmentStatus,
  BonusAssignment,
  BonusDisbursement,
  PaymentSource,
  PaymentStatus,
} from 'api/gql-utils/bonusAssignment/types';
import { PAISE_PER_RUPEE } from '../components/AllocateBonusModal/constants';
import {
  PAYMENT_SOURCE_TO_BONUS_SOURCE,
  PAYMENT_STATUS_TO_BONUS_STATUS,
} from './constants';
import { BonusRow } from './types';

export const MAX_PAGES = 50;

export interface PagedResult<T> {
  items: T[];
  totalCount: number;
}

export const fetchAllPages = async <T>(
  fetchPage: (offset: number, limit: number) => Promise<PagedResult<T>>,
  pageSize: number,
  label = 'bonus list',
): Promise<T[]> => {
  const all: T[] = [];

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const { items, totalCount } = await fetchPage(page * pageSize, pageSize);
    all.push(...items);

    if (items.length === 0 || items.length < pageSize) return all;
    if (all.length >= totalCount) return all;

    if (page === MAX_PAGES - 1) {
      captureErrorInSentry(
        `fetchAllPages hit the MAX_PAGES cap draining ${label}: kept ${all.length} of ` +
          `${totalCount} rows (pageSize ${pageSize}, cap ${MAX_PAGES} pages). ` +
          `Rows beyond the cap are missing from the view.`,
      );
    }
  }

  return all;
};

const toDateString = (payrollMonth: string | null | undefined) =>
  payrollMonth ? `${payrollMonth}-01` : null;

const toRupees = (amount: BonusDisbursement['amount']): number | null => {
  const paise = Number(amount);
  return Number.isFinite(paise) ? paise / PAISE_PER_RUPEE : null;
};

const toBonusStatus = (paymentStatus: BonusDisbursement['payment_status']): number | null => {
  if (!paymentStatus) return null;
  const mapped = PAYMENT_STATUS_TO_BONUS_STATUS[paymentStatus];
  if (mapped === undefined) return null;
  const status = Number(mapped);
  return Number.isFinite(status) ? status : null;
};

const toRow = (
  assignment: BonusAssignment,
  disbursement: BonusDisbursement,
  componentNames: Record<string, string>,
): BonusRow => ({
  id: disbursement.id,
  assignment_id: assignment.id,
  bonus_component_id: assignment.bonus_component_id,
  bonus_name:
    componentNames[assignment.bonus_component_id] ?? assignment.bonus_component_id ?? null,
  amount: toRupees(disbursement.amount),
  source: assignment.payment_source
    ? PAYMENT_SOURCE_TO_BONUS_SOURCE[assignment.payment_source] ?? null
    : null,
  payout_month: toDateString(disbursement.payroll_month),
  clawback_applicable: assignment.clawback_applicable,
  clawback_period: assignment.clawback_period,
  clawback_month: toDateString(disbursement.clawback_fnf_payroll_month),
  remarks: assignment.remarks,
  status: toBonusStatus(disbursement.payment_status),
  created_at: assignment.created_at,
  updated_at: assignment.updated_at,
});

export const mapAssignmentsToRows = (
  assignments: BonusAssignment[],
  componentNames: Record<string, string>,
): BonusRow[] =>
  assignments
    .filter((assignment) => assignment.assignment_status !== AssignmentStatus.CANCELLED)
    .flatMap((assignment) =>
      (assignment.disbursements ?? [])
        .filter((disbursement) => disbursement.payment_status !== PaymentStatus.CANCELLED)
        .map((disbursement) => toRow(assignment, disbursement, componentNames)),
    );
